import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { useCallProvider, type CallContext, type PlaceCallResult } from './useCallProvider';

/**
 * O que os botões de "Ligar" usam. Embrulha o useCallProvider com o feedback ao usuário,
 * para os ~14 call sites não repetirem a mesma árvore de toasts (e não divergirem, como
 * acontece hoje: alguns avisam, outros falham em silêncio).
 */
export function useClickToCall() {
  const { placeCall, provider, canCall, isProcessing, sonax } = useCallProvider();

  const call = useCallback(
    async (phone: string | null | undefined, ctx?: CallContext): Promise<PlaceCallResult> => {
      const result = await placeCall(phone, ctx);

      if (result.ok) {
        if (result.provider === 'softphone') {
          // Não há como detectar se o MicroSIP abriu (nenhum evento de erro existe).
          // Em vez de fingir certeza, damos o número e uma saída manual.
          toast({
            title: 'Abrindo o MicroSIP…',
            description: `Discando ${result.numero}. Se não abrir, verifique se o MicroSIP está instalado e é o aplicativo padrão para links sip:.`,
          });
        }
        return result;
      }

      switch (result.reason) {
        case 'no-phone':
          toast({
            title: 'Telefone não disponível',
            description: 'Número de telefone não encontrado.',
            variant: 'destructive',
          });
          break;
        case 'invalid-phone':
          toast({
            title: 'Número inválido',
            description: 'Não consegui montar o número para discagem (esperado DDD + telefone).',
            variant: 'destructive',
          });
          break;
        case 'not-configured':
          toast({
            title: 'Widget não configurado',
            description: 'Configure o script do Sonax nas configurações para fazer ligações.',
            variant: 'destructive',
          });
          break;
        case 'invalid-template':
          toast({
            title: 'Modelo de discagem inválido',
            description: 'Revise o modelo do link de discagem no seu Perfil.',
            variant: 'destructive',
          });
          break;
        default:
          toast({
            title: 'Não foi possível iniciar a ligação',
            description: 'Tente novamente em instantes.',
            variant: 'destructive',
          });
      }

      return result;
    },
    [placeCall],
  );

  return { call, provider, canCall, isProcessing, sonax };
}
